import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { 
  Inbox, 
  CheckCircle2, 
  XCircle, 
  ArrowRight, 
  Calendar, 
  Phone, 
  Clock, 
  User, 
  Building, 
  Mail, 
  MapPin,
  Loader2,
  Eye,
  Trash2,
  Users,
  UserPlus,
  ChevronsUpDown,
  Check,
  Sparkles,
  AlertTriangle,
  Send
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import type { EventRequest, Region, UserRole, FunnelSubmission } from "@shared/schema";

interface IntakePageProps {
  effectiveRole?: UserRole;
}

function formatDate(date: Date | string | null): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(date: Date | string | null): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getRequestTypeIcon(type: string) {
  switch (type) {
    case "call_request":
      return <Phone className="h-4 w-4" />;
    case "waitlist":
      return <Clock className="h-4 w-4" />;
    default:
      return <Calendar className="h-4 w-4" />;
  }
}

function getRequestTypeLabel(type: string): string {
  switch (type) {
    case "call_request":
      return "Call Request";
    case "waitlist":
      return "Waitlist";
    default:
      return "Event Request";
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case "approved":
      return <Badge className="bg-green-500/10 text-green-600 border-green-200">Approved</Badge>;
    case "rejected":
      return <Badge className="bg-red-500/10 text-red-600 border-red-200">Rejected</Badge>;
    case "converted":
      return <Badge className="bg-blue-500/10 text-blue-600 border-blue-200">Converted</Badge>;
    default:
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-200">Pending</Badge>;
  }
}

type EnrichedEventRequest = EventRequest & { eventOfferingName?: string | null };

interface RequestDetailsDialogProps {
  request: EnrichedEventRequest | null;
  open: boolean;
  onClose: () => void;
  regions: Region[];
}

function RequestDetailsDialog({ request, open, onClose, regions }: RequestDetailsDialogProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState(request?.notes || "");
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [showConvertDialog, setShowConvertDialog] = useState(false);

  useEffect(() => {
    if (request) {
      setNotes(request.notes || "");
      setSelectedRegionId("");
      setShowConvertDialog(false);
    }
  }, [request?.id]);

  const updateMutation = useMutation({
    mutationFn: async ({ status, notes }: { status?: string; notes?: string }) => {
      if (!request) return;
      const response = await apiRequest("PATCH", `/api/event-requests/${request.id}`, { status, notes });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approval-notifications/count"] });
      toast({ title: "Request updated" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update request",
        variant: "destructive",
      });
    },
  });

  const convertMutation = useMutation({
    mutationFn: async () => {
      if (!request || !selectedRegionId) return;
      const response = await apiRequest("POST", `/api/event-requests/${request.id}/convert-to-lead`, { 
        regionId: selectedRegionId 
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approval-notifications/count"] });
      toast({ title: "Converted to lead successfully" });
      setShowConvertDialog(false);
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to convert to lead",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!request) return;
      await apiRequest("DELETE", `/api/event-requests/${request.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approval-notifications/count"] });
      toast({ title: "Request deleted" });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete request",
        variant: "destructive",
      });
    },
  });

  // Tier-A #5: admin-recovery action for submissions whose notification
  // email failed at create time. Re-fires the same admin notification.
  const resendNotificationMutation = useMutation({
    mutationFn: async () => {
      if (!request) return;
      const response = await apiRequest("POST", `/api/event-requests/${request.id}/resend-notification`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-requests"] });
      toast({ title: "Notification resent" });
    },
    onError: (error: any) => {
      toast({
        title: "Resend failed",
        description: error.message || "Could not re-send the admin notification.",
        variant: "destructive",
      });
    },
  });

  if (!request) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              {getRequestTypeIcon(request.requestType)}
              <DialogTitle>{getRequestTypeLabel(request.requestType)}</DialogTitle>
              {getStatusBadge(request.status)}
            </div>
            <DialogDescription>
              Submitted {formatDateTime(request.createdAt)}
            </DialogDescription>
            {/* Tier-A #5: admin-recovery banner for submissions whose
                notification email failed at create time. The submission
                itself was preserved — the admin can re-send from here. */}
            {(request as any).notificationStatus === 'failed' && (
              <div
                data-testid="banner-notification-failed"
                className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/40"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="flex-1">
                  <div className="font-medium text-amber-900 dark:text-amber-100">
                    Admin notification email failed to send
                  </div>
                  <div className="mt-0.5 text-amber-800 dark:text-amber-200">
                    {(request as any).notificationError
                      ? <span>Reason: <span className="font-mono text-xs">{(request as any).notificationError}</span></span>
                      : 'No error details captured.'}
                    {((request as any).notificationAttemptCount ?? 0) > 0 && (
                      <span className="ml-2 text-xs opacity-75">
                        ({(request as any).notificationAttemptCount} attempt{(request as any).notificationAttemptCount === 1 ? '' : 's'})
                      </span>
                    )}
                  </div>
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resendNotificationMutation.mutate()}
                      disabled={resendNotificationMutation.isPending}
                      data-testid="button-resend-notification"
                    >
                      {resendNotificationMutation.isPending
                        ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Resending…</>
                        : <><Send className="mr-2 h-3 w-3" />Resend notification</>}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogHeader>

          <div className="grid gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium w-24">Name:</span>
                  <span>{request.contactName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${request.email}`} className="text-primary hover:underline">
                    {request.email}
                  </a>
                </div>
                {request.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${request.phone}`} className="text-primary hover:underline">
                      {request.phone}
                    </a>
                  </div>
                )}
                {request.companyName && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{request.companyName}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {request.requestType === "event_request" && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Event Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-2 text-sm">
                  {request.eventOfferingName && (
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium w-32">Show:</span>
                      <span>{request.eventOfferingName}</span>
                    </div>
                  )}
                  {request.preferredDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium w-32">Preferred Date:</span>
                      <span>{formatDate(request.preferredDate)}</span>
                    </div>
                  )}
                  {request.preferredCallTime && request.requestType === "event_request" && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium w-32">Preferred Time:</span>
                      <span>{request.preferredCallTime}</span>
                    </div>
                  )}
                  {request.alternateDate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium w-32">Alternate Date:</span>
                      <span>{formatDate(request.alternateDate)}</span>
                    </div>
                  )}
                  {request.estimatedAttendees && (
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium w-32">Attendees:</span>
                      <span>{request.estimatedAttendees}</span>
                    </div>
                  )}
                  {request.cityState && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium w-32">Location:</span>
                      <span>{request.cityState}</span>
                    </div>
                  )}
                  {(request.venueAddress || request.venueCity) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <span className="font-medium w-32">Venue:</span>
                      <span>
                        {[request.venueAddress, request.venueCity, request.venueState, request.venueZip]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                  {!request.eventOfferingName && !request.preferredDate && !request.alternateDate && !request.estimatedAttendees && !request.venueAddress && !request.venueCity && !request.cityState && !request.preferredCallTime && (
                    <p className="text-muted-foreground italic">No event details were provided with this request.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {request.requestType === "call_request" && request.preferredCallTime && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Call Preferences
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Best time:</span>
                    <span className="capitalize">{request.preferredCallTime}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {request.requestType === "waitlist" && request.preferredTimeframe && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Waitlist Preferences
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Preferred timeframe:</span>
                    <span className="capitalize">{request.preferredTimeframe}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {request.eventDetails && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Additional Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{request.eventDetails}</p>
                </CardContent>
              </Card>
            )}

            <div>
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add internal notes about this request..."
                rows={3}
                data-testid="textarea-notes"
              />
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => updateMutation.mutate({ notes })}
                disabled={updateMutation.isPending}
                data-testid="button-save-notes"
              >
                Save Notes
              </Button>
            </div>
          </div>

          {request.status === "pending" && (
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="destructive"
                onClick={() => updateMutation.mutate({ status: "rejected" })}
                disabled={updateMutation.isPending}
                data-testid="button-reject"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => updateMutation.mutate({ status: "approved" })}
                disabled={updateMutation.isPending}
                data-testid="button-approve"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve
              </Button>
              <Button
                onClick={() => setShowConvertDialog(true)}
                disabled={updateMutation.isPending}
                data-testid="button-convert-to-lead"
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Convert to Lead
              </Button>
            </DialogFooter>
          )}

          {(request.status === "approved" || request.status === "rejected") && (
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                data-testid="button-delete-request"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Request
              </Button>
              <Button
                onClick={() => setShowConvertDialog(true)}
                data-testid="button-convert-to-lead"
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Convert to Lead
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Lead</DialogTitle>
            <DialogDescription>
              Select a region to create a new lead from this request.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Region</Label>
            <Select value={selectedRegionId} onValueChange={setSelectedRegionId}>
              <SelectTrigger data-testid="select-region">
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={String(region.id)}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => convertMutation.mutate()}
              disabled={!selectedRegionId || convertMutation.isPending}
              data-testid="button-confirm-convert"
            >
              {convertMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="mr-2 h-4 w-4" />
              )}
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function IntakePage({ effectiveRole }: IntakePageProps) {
  const { toast } = useToast();
  const [selectedRequest, setSelectedRequest] = useState<EnrichedEventRequest | null>(null);
  const [activeTab, setActiveTab] = useState("pending");

  const { data: requests = [], isLoading } = useQuery<EnrichedEventRequest[]>({
    queryKey: ["/api/event-requests"],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  // Check if user has approval permission (admin or has canApprove assignment)
  const { data: approvalStatus } = useQuery<{ count: number; canApprove: boolean }>({
    queryKey: ["/api/approval-notifications/count"],
  });
  const hasApprovalPermission = effectiveRole === "admin" || approvalStatus?.canApprove;

  const { data: funnelSubmissions = [], isLoading: loadingSubmissions } = useQuery<FunnelSubmission[]>({
    queryKey: ["/api/funnel-submissions"],
    enabled: hasApprovalPermission === true,
  });

  // Mark all approvals as viewed when page loads - this clears the notification badge
  // Only runs if user has approval permission
  useEffect(() => {
    if (!hasApprovalPermission) return;
    
    const markAsViewed = async () => {
      try {
        await apiRequest("POST", "/api/approval-notifications/mark-viewed");
        // Invalidate the count query to update the badge
        queryClient.invalidateQueries({ queryKey: ["/api/approval-notifications/count"] });
      } catch (error) {
        console.error("Failed to mark approvals as viewed:", error);
      }
    };
    markAsViewed();
  }, [hasApprovalPermission]); // Run when permission is determined

  const deleteFunnelSubmissionMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/funnel-submissions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/funnel-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approval-notifications/count"] });
      toast({ title: "Submission deleted" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete submission",
        variant: "destructive",
      });
    },
  });

  // State for convert submission to lead dialog
  const [convertSubmissionDialog, setConvertSubmissionDialog] = useState<{
    open: boolean;
    submission: FunnelSubmission | null;
  }>({ open: false, submission: null });
  const [convertSubmissionRegion, setConvertSubmissionRegion] = useState("");
  const [convertSubmissionNotes, setConvertSubmissionNotes] = useState("");
  const [regionPopoverOpen, setRegionPopoverOpen] = useState(false);

  const convertSubmissionMutation = useMutation({
    mutationFn: async ({ id, regionId, notes }: { id: string; regionId: string; notes: string }) => {
      const response = await apiRequest("POST", `/api/funnel-submissions/${id}/convert-to-lead`, { regionId, notes });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/funnel-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approval-notifications/count"] });
      toast({ title: "Converted to lead successfully" });
      setConvertSubmissionDialog({ open: false, submission: null });
      setConvertSubmissionRegion("");
      setConvertSubmissionNotes("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to convert to lead",
        variant: "destructive",
      });
    },
  });

  const pendingRequests = requests.filter(r => r.status === "pending");
  const approvedRequests = requests.filter(r => r.status === "approved");
  const rejectedRequests = requests.filter(r => r.status === "rejected");
  const convertedRequests = requests.filter(r => r.status === "converted");

  const getRequestsForTab = () => {
    switch (activeTab) {
      case "approved":
        return approvedRequests;
      case "rejected":
        return rejectedRequests;
      case "converted":
        return convertedRequests;
      case "all":
        return requests;
      case "submissions":
        return [];
      default:
        return pendingRequests;
    }
  };

  const formatSubmissionDate = (date: Date | string | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Approvals</h1>
        <p className="text-muted-foreground">Review and manage incoming show requests</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <Card className="hover-elevate cursor-pointer" onClick={() => setActiveTab("pending")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{pendingRequests.length}</p>
              </div>
              <Inbox className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-elevate cursor-pointer" onClick={() => setActiveTab("approved")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">{approvedRequests.length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-elevate cursor-pointer" onClick={() => setActiveTab("converted")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Converted</p>
                <p className="text-2xl font-bold">{convertedRequests.length}</p>
              </div>
              <ArrowRight className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="hover-elevate cursor-pointer" onClick={() => setActiveTab("rejected")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold">{rejectedRequests.length}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pendingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              Approved ({approvedRequests.length})
            </TabsTrigger>
            <TabsTrigger value="converted" data-testid="tab-converted">
              Converted ({convertedRequests.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">
              Rejected ({rejectedRequests.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              All ({requests.length})
            </TabsTrigger>
          </TabsList>
          
          {hasApprovalPermission && (
            <TabsList>
              <TabsTrigger value="submissions" data-testid="tab-submissions">
                <Users className="h-4 w-4 mr-2" />
                Submissions ({funnelSubmissions.length})
              </TabsTrigger>
            </TabsList>
          )}
        </div>

        {activeTab === "submissions" ? (
          <TabsContent value="submissions" className="mt-4">
            {loadingSubmissions ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : funnelSubmissions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No incomplete submissions</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Submissions appear here when visitors start but don't complete the booking funnel.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {funnelSubmissions.map((submission) => (
                  <Card key={submission.id} data-testid={`submission-card-${submission.id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-muted rounded-lg">
                            <User className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{submission.contactName}</span>
                              <Badge variant="outline" className="text-yellow-600">Incomplete</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <Mail className="h-3 w-3" />
                              <span>{submission.email}</span>
                              {submission.phone && (
                                <>
                                  <span>|</span>
                                  <Phone className="h-3 w-3" />
                                  <span>{submission.phone}</span>
                                </>
                              )}
                            </div>
                            {submission.companyName && (
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Building className="h-3 w-3" />
                                <span>{submission.companyName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right text-sm text-muted-foreground mr-2">
                            {formatSubmissionDate(submission.createdAt)}
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setConvertSubmissionDialog({ open: true, submission });
                              setConvertSubmissionRegion("");
                              setConvertSubmissionNotes("");
                            }}
                            data-testid={`convert-submission-${submission.id}`}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Convert to Lead
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => deleteFunnelSubmissionMutation.mutate(submission.id)}
                            disabled={deleteFunnelSubmissionMutation.isPending}
                            data-testid={`delete-submission-${submission.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ) : (
        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : getRequestsForTab().length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No {activeTab} requests</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {getRequestsForTab().map((request) => (
                <Card 
                  key={request.id} 
                  className="hover-elevate cursor-pointer"
                  onClick={() => setSelectedRequest(request)}
                  data-testid={`request-card-${request.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-muted rounded-lg">
                          {getRequestTypeIcon(request.requestType)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{request.contactName}</span>
                            {getStatusBadge(request.status)}
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>{getRequestTypeLabel(request.requestType)}</span>
                            {request.companyName && (
                              <>
                                <span>-</span>
                                <span>{request.companyName}</span>
                              </>
                            )}
                            {/* Task #599 — flag inquiries the AI Lead Catcher
                                already auto-replied to so the rep knows the
                                prospect's email is not "cold first contact". */}
                            {typeof request.notes === "string" && request.notes.startsWith("[AI Catcher]") && (
                              <Badge
                                variant="secondary"
                                className="gap-1 bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200"
                                data-testid={`badge-ai-catcher-${request.id}`}
                              >
                                <Sparkles className="h-3 w-3" />
                                AI Catcher
                              </Badge>
                            )}
                          </div>
                          {request.preferredDate && (
                            <div className="text-sm text-muted-foreground">
                              Preferred: {formatDate(request.preferredDate)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm text-muted-foreground">
                          {formatDateTime(request.createdAt)}
                        </div>
                        <Button size="icon" variant="ghost" data-testid={`view-request-${request.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        )}
      </Tabs>

      <RequestDetailsDialog
        request={selectedRequest}
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        regions={regions}
      />

      {/* Convert Submission to Lead Dialog */}
      <Dialog 
        open={convertSubmissionDialog.open} 
        onOpenChange={(open) => !open && setConvertSubmissionDialog({ open: false, submission: null })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Convert to Lead
            </DialogTitle>
            <DialogDescription>
              Convert this incomplete submission into a lead record.
            </DialogDescription>
          </DialogHeader>
          
          {convertSubmissionDialog.submission && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{convertSubmissionDialog.submission.contactName}</p>
                <p className="text-sm text-muted-foreground">{convertSubmissionDialog.submission.email}</p>
                {convertSubmissionDialog.submission.phone && (
                  <p className="text-sm text-muted-foreground">{convertSubmissionDialog.submission.phone}</p>
                )}
                {convertSubmissionDialog.submission.companyName && (
                  <p className="text-sm text-muted-foreground">{convertSubmissionDialog.submission.companyName}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Region *</Label>
                <Popover open={regionPopoverOpen} onOpenChange={setRegionPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={regionPopoverOpen}
                      className="w-full justify-between"
                      data-testid="select-submission-region"
                    >
                      {convertSubmissionRegion
                        ? regions.find((r) => String(r.id) === convertSubmissionRegion)?.name
                        : "Select a region..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Search regions..." />
                      <CommandList>
                        <CommandEmpty>No region found.</CommandEmpty>
                        <CommandGroup>
                          {regions.map((region) => (
                            <CommandItem
                              key={region.id}
                              value={region.name}
                              onSelect={() => {
                                setConvertSubmissionRegion(String(region.id));
                                setRegionPopoverOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  convertSubmissionRegion === String(region.id) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {region.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add any notes about this lead..."
                  value={convertSubmissionNotes}
                  onChange={(e) => setConvertSubmissionNotes(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="input-submission-notes"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConvertSubmissionDialog({ open: false, submission: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (convertSubmissionDialog.submission && convertSubmissionRegion) {
                  convertSubmissionMutation.mutate({
                    id: convertSubmissionDialog.submission.id,
                    regionId: convertSubmissionRegion,
                    notes: convertSubmissionNotes,
                  });
                }
              }}
              disabled={!convertSubmissionRegion || convertSubmissionMutation.isPending}
              data-testid="button-confirm-convert-submission"
            >
              {convertSubmissionMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="mr-2 h-4 w-4" />
              )}
              Convert to Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
